import { normGoal } from "../api/achievementsApi";
import { isObj, list } from "../api/normView";
import type { AchGoal, AchievementsView } from "../model/types";
import { GOAL_SHELF_KEY } from "./goalShelfKey";
import { goalRows } from "./goals";
import { achLang } from "./lang";

/**
 * Полка строк-подсказок (как полка достижений в приложении, AchievementsStore):
 * последние свои цели этого браузера, чтобы строка «что сделать → что получите»
 * была известна в первый кадр экрана, а не появлялась через секунду над
 * кнопками (план §1.2: над кнопками — только решённое на входе).
 *
 * Хранится одна запись: чьи (отпечаток токена сессии, сам токен — нет), язык,
 * когда, строки. Другой вход, другой язык, старше двух недель — полки нет;
 * выход из аккаунта её стирает (lib/goalShelfKey). Хранилище недоступно —
 * полки нет; подсказка просто подождёт следующего захода.
 *
 * Модуль лёгкий — ни каталога, ни картинок.
 */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_ROWS = 160;

const sessionToken = (): string => {
    try {
        return (localStorage.getItem("site_session_token") || "").trim();
    } catch {
        return "";
    }
};

/** Отпечаток токена (FNV-1a, 32 бита): узнать «тот же вход», не храня сам токен. */
export const shelfOwner = (token: string): string => {
    let h = 0x811c9dc5;
    for (let i = 0; i < token.length; i += 1) {
        h ^= token.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `${h.toString(16)}.${token.length}`;
};

/** Строки полки для этого входа и языка; нет — null. */
export const readGoalShelf = (lang: string): AchGoal[] | null => {
    const token = sessionToken();
    if (!token) return null;
    let raw: unknown;
    try {
        raw = JSON.parse(localStorage.getItem(GOAL_SHELF_KEY) || "null");
    } catch {
        return null;
    }
    if (!isObj(raw)) return null;
    if (raw.who !== shelfOwner(token) || raw.lang !== achLang(lang)) return null;
    const age = Date.now() - (typeof raw.at === "number" ? raw.at : 0);
    if (!(age >= 0 && age < MAX_AGE_MS)) return null;
    return list(raw.rows, normGoal);
};

/** Свежий свой вид → на полку; null (сервер не знает человека, нет входа) — полку убрать. */
export const writeGoalShelf = (lang: string, view: AchievementsView | null): void => {
    try {
        const token = sessionToken();
        if (!token || !view) {
            localStorage.removeItem(GOAL_SHELF_KEY);
            return;
        }
        const rows = goalRows(view).slice(0, MAX_ROWS);
        localStorage.setItem(
            GOAL_SHELF_KEY,
            JSON.stringify({ who: shelfOwner(token), lang: achLang(lang), at: Date.now(), rows }),
        );
    } catch {
        /* хранилище полно или закрыто — без полки */
    }
};

export { GOAL_SHELF_KEY };
